import React from 'react';
import { Box, SxProps, Theme, Typography } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { FONT_ENUM } from '../../types/global/fonts.types';

interface RichTextEditorProps {
    value: string;
    onChange: (value: string) => void;
    label?: string;
    labelSx?: SxProps<Theme>;
    minChar?: number;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, labelSx, label, minChar }) => {
    const { themeStyle } = useThemeContext();

    const modules = {
        toolbar: [
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ color: [] }, { background: [] }],
            ['link', 'image'],
            ['clean'],
        ],
    };

    // Calculate character count (strip HTML tags for accurate count)
    const getTextContent = (html: string) => {
        const div = document.createElement('div');
        div.innerHTML = html;
        return div.textContent || div.innerText || '';
    };

    const characterCount = getTextContent(value).length;
    const isMinCharMet = minChar ? characterCount >= minChar : true;

    return (
        <Box
            sx={{
                position: 'relative',
                '& .ql-container': {
                    backgroundColor: theme[themeStyle].secondaryBg,
                    color: theme[themeStyle].primary_white,
                    border: `1px solid ${theme[themeStyle].main_blue}`,
                    borderTop: 'none',
                    borderRadius: '0 0 8px 8px',
                },
                '& .ql-toolbar': {
                    backgroundColor: theme[themeStyle].secondaryBg,
                    border: `1px solid ${theme[themeStyle].highlight}`,
                    borderBottom: 'none',
                    borderRadius: '8px 8px 0 0',
                    '& .ql-picker': {
                        color: theme[themeStyle].primary_white,
                    },
                    '& .ql-stroke': {
                        stroke: theme[themeStyle].primary_white,
                    },
                    '& .ql-fill': {
                        fill: theme[themeStyle].primary_white,
                    },
                },
                width: '100%',
            }}>
            {!!label && (
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        color: theme[themeStyle].tag_white,
                        fontSize: 12,
                        mb: 2,
                        ...labelSx,
                    }}>
                    {label}
                </Typography>
            )}
            <ReactQuill value={value} onChange={onChange} modules={modules} theme="snow" />
            {minChar && (
                <Typography
                    sx={{
                        fontSize: 11,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        color: isMinCharMet ? theme[themeStyle].green_status_indicator : theme[themeStyle].primary_red,
                    }}>
                    Min char. {characterCount}/{minChar}
                </Typography>
            )}
        </Box>
    );
};
